import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import ReadMoreTruncateText from '../../utils/ReadMoreTruncateText';
import TrimContent from '../../utils/TrimContent';
import PaginationControls from '../../utils/PaginationControls';
import DuplicateReferencesModal from './DuplicateReferencesModal';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DocumentPreview from '../../utils/DocumentPreview';

const API_BASE = '/pceapi/genbi/reflib';

const ReferenceLibrary = ({ onClose, onCountChange }) => {
  const navigate = useNavigate();
  const headers = { Authorization: GetCookie('access_token') };

  const [references, setReferences] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // Editing state
  const [editingRows, setEditingRows] = useState(new Set());
  const [editedData, setEditedData] = useState({});
  const [hasGlobalChanges, setHasGlobalChanges] = useState(false);

  // Duplicates modal
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateSourceRef, setDuplicateSourceRef] = useState(null);

  // Linked docs expand
  const [expandedLinkedDocs, setExpandedLinkedDocs] = useState(null);

  // Popups
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showError, setShowError] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Document preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);

  useEffect(() => {
    loadReferences();
  }, [page, size, search, sortField, sortDirection]);

  useEffect(() => {
    if (onCountChange) onCountChange(total);
  }, [total]);

  const loadReferences = async () => {
    setIsProcessing(true);
    setProcessingMsg('Loading Reference Library');

    try {
      const response = await axios.post(
        `${API_BASE}/list`,
        {
          page,
          size,
          search: search || null,
          sort_field: sortField,
          sort_direction: sortDirection,
        },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessing(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setIsProcessing(false);
        setErrorMsg(data.message || 'Failed to load Reference Library');
        setShowError(true);
        return;
      }


      const mappedRefs = (data.data || []).map((r) => ({
        id: r.ar_id,
        title: r.title || '',
        authors: r.authors || [],
        year: r.year || '',
        doi: r.doi || '',
        publisher: r.publisher || '',
        journal_page_no: r.journal_page_no || '',
        organization: r.organization || '',
        address: r.address || '',
        classification: r.classification || '',
        website_url: r.website_url || '',
        accessed_date: r.accessed_date || '',
        linked_docs: (r.linked_docs || []).map((d) => ({
          doc_id: d.doc_id,
          doc_name: d.document_name,
        })),
      }));

      setReferences(mappedRefs);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('loadReferences error:', error);
      setErrorMsg(error.message || 'Failed to load Reference Library');
      setShowError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const saveAllChanges = async () => {
    const editedIds = Object.keys(editedData);
    if (editedIds.length === 0) return;

    setIsProcessing(true);
    setProcessingMsg('Saving reference changes');

    try {
      const requests = editedIds.map((arId) => {
        const updates = { ...editedData[arId] };
        if (typeof updates.authors === 'string') {
          updates.authors = updates.authors
            .split(',')
            .map((a) => a.trim())
            .filter((a) => a.length > 0);
        }
        return axios.post(
          `${API_BASE}/update`,
          {
            ar_id: parseInt(arId, 10),
            updates,
          },
          { headers }
        );
      });

      const responses = await Promise.allSettled(requests);

      const failed = [];
      responses.forEach((res, i) => {
        const arId = editedIds[i];
        if (res.status === 'rejected') {
          failed.push(`AR ID ${arId}: ${res.reason?.message || 'request failed'}`);
        } else if (res.value?.data?.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
        } else if (res.value?.data?.status === 'failed') {
          failed.push(`AR ID ${arId}: ${res.value.data.message || 'update failed'}`);
        }
      });

      setEditingRows(new Set());
      setEditedData({});
      setHasGlobalChanges(false);

      if (failed.length === 0) {
        setSuccessMsg('All reference metadata saved successfully');
        setShowSuccess(true);
      } else {
        setErrorMsg(
          `${editedIds.length - failed.length} succeeded, ${failed.length} failed:\n${failed.join('\n')}`
        );
        setShowError(true);
      }

      loadReferences();
    } catch (error) {
      console.error('saveAllChanges error:', error);
      setErrorMsg(error.message || 'Failed to save reference changes');
      setShowError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMerge = async (survivorId, victimIds) => {
    if (!victimIds || victimIds.length === 0) return;

    setIsProcessing(true);
    setProcessingMsg('Merging references');

    try {
      // Backend merges one victim at a time
      const requests = victimIds.map((victimId) =>
        axios.post(
          `${API_BASE}/merge`,
          {
            survivor_ar_id: survivorId,
            victim_ar_id: victimId,
          },
          { headers }
        )
      );

      const responses = await Promise.allSettled(requests);

      const failed = [];
      responses.forEach((res, i) => {
        const vId = victimIds[i];
        if (res.status === 'rejected') {
          failed.push(`AR ID ${vId}: ${res.reason?.message || 'request failed'}`);
        } else if (res.value?.data?.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
        } else if (res.value?.data?.status === 'failed') {
          failed.push(`AR ID ${vId}: ${res.value.data.message || 'merge failed'}`);
        }
      });

      if (failed.length === 0) {
        const refLabel = victimIds.length > 1 ? 'References' : 'Reference';
        setSuccessMsg(
          `${refLabel} ID ${victimIds.join(', ')} merged into ID ${survivorId} successfully`
        );
        setShowSuccess(true);
      } else {
        setErrorMsg(
          `${victimIds.length - failed.length} succeeded, ${failed.length} failed:\n${failed.join('\n')}`
        );
        setShowError(true);
      }

      loadReferences();
    } catch (error) {
      console.error('handleMerge error:', error);
      setErrorMsg(error.message || 'Failed to merge references');
      setShowError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefresh = () => {
    setEditingRows(new Set());
    setEditedData({});
    setHasGlobalChanges(false);
    loadReferences();
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    return (
      <span className="ms-1">
        {sortDirection === 'asc' ? (
          <i className="bi bi-caret-up-fill"></i>
        ) : (
          <i className="bi bi-caret-down-fill"></i>
        )}
      </span>
    );
  };

  const handleEditRow = (arId) => {
    setEditingRows(new Set([...editingRows, arId]));
  };

  const handleCancelEdit = (arId) => {
    const newEditing = new Set(editingRows);
    newEditing.delete(arId);
    setEditingRows(newEditing);
    const newEdited = { ...editedData };
    delete newEdited[arId];
    setEditedData(newEdited);
    setHasGlobalChanges(Object.keys(newEdited).length > 0);
  };

  const handleFieldChange = (arId, fieldName, value) => {
    const updated = { ...editedData };
    if (!updated[arId]) updated[arId] = {};
    updated[arId][fieldName] = value;
    setEditedData(updated);
    setHasGlobalChanges(true);
  };

  const saveRowChanges = (arId) => {
    const newEditing = new Set(editingRows);
    newEditing.delete(arId);
    setEditingRows(newEditing);
    setHasGlobalChanges(Object.keys(editedData).length > 0);
  };

  const getFieldValue = (ref, fieldName) => {
    if (editedData[ref.id] && editedData[ref.id][fieldName] !== undefined) {
      const val = editedData[ref.id][fieldName];
      if (fieldName === 'authors') return Array.isArray(val) ? val.join(', ') : val;
      return val;
    }
    const original = ref[fieldName];
    if (fieldName === 'authors') return Array.isArray(original) ? original.join(', ') : original || '';
    return original || '';
  };

  const renderEditableField = (ref, fieldName, useReadMore = false, readMoreLimit = 50) => {
    const isEditing = editingRows.has(ref.id);
    const value = getFieldValue(ref, fieldName);

    if (!isEditing) {
      if (useReadMore && value && value.length > readMoreLimit) {
        return <ReadMoreTruncateText text={value} limit={readMoreLimit} />;
      }
      return <div className="ev-small-p-dark">{value || <span className="ev-small-p-dark">-</span>}</div>;
    }

    return (
      <textarea
        className="form-control formField3 scrollable-element vh-10"
        style={{ resize: 'none' }}
        value={value}
        onChange={(e) => handleFieldChange(ref.id, fieldName, e.target.value)}
      />
    );
  };

  const handleViewDuplicates = (ref) => {
    setDuplicateSourceRef(ref);
    setDuplicateModalOpen(true);
  };

  const handlePreviewDocument = (doc_id, documentName) => {
    setPreviewDocId(doc_id);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  const totalPages = Math.ceil(total / size);

  return (
    <div className="ev-card-4 w-100 ev-padding-large">
      {/* <div className="col-3 d-flex align-items-center">
          <p className="ev-reg-p-blue mb-0">
            <i className="bi bi-book me-1"></i>Reference Library
          </p>
        </div> */}
      <div className="row mt-1 mb-3">
        <div className="col-8 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search by ID, Title, Author, DOI, Publisher..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
          />
          <button className="ev-btn-lightblue-fill-round search-bar-button" onClick={handleSearch}>
            <i className="bi bi-search"></i>
          </button>
        </div>

        <div className="col-4 d-flex justify-content-end align-items-center flex-wrap gap-2">
          {hasGlobalChanges && (
            <button className="ev-btn-yellow-fill-round ev-bold-reg" onClick={saveAllChanges}>
              <i className="bi bi-check-circle"></i> Update References
            </button>
          )}
          <button className="ev-btn-lightblue-fill-round ev-bold-reg" onClick={handleRefresh}>
            <i className="bi bi-arrow-repeat"></i> Refresh
          </button>
        </div>
      </div>

      {references.length === 0 && !isProcessing ? (
        <div className="alert alert-primary">No references found in the library.</div>
      ) : (
        <>
          <div className="scrollable-element vh-75" >
            <table className="table-custom1 mt-0 table-fixed" style={{ minWidth: '2280px'}}>
              <colgroup>
                <col style={{ width: '60px' }} />     {/* AR ID */}
                <col style={{ width: '300px' }} />    {/* Title */}
                <col style={{ width: '200px' }} />    {/* Authors */}
                <col style={{ width: '60px' }} />     {/* Year */}
                <col style={{ width: '180px' }} />    {/* DOI */}
                <col style={{ width: '140px' }} />    {/* Publisher */}
                <col style={{ width: '180px' }} />    {/* Journal/Pages */}
                <col style={{ width: '220px' }} />    {/* Website URL */}
                <col style={{ width: '140px' }} />    {/* Accessed Date */}
                <col style={{ width: '160px' }} />    {/* Linked Docs */}
                <col style={{ width: '160px' }} />    {/* Actions */}
              </colgroup>
              <thead>
                <tr>
                  <th><label>ID</label></th>
                  <th onClick={() => handleSortChange('title')}>
                    <label>Title {renderSortArrow('title')}</label>
                  </th>
                  <th onClick={() => handleSortChange('authors')}>
                    <label>Authors {renderSortArrow('authors')}</label>
                  </th>
                  <th onClick={() => handleSortChange('year')}>
                    <label>Year {renderSortArrow('year')}</label>
                  </th>
                  <th onClick={() => handleSortChange('doi')}>
                    <label>DOI {renderSortArrow('doi')}</label>
                  </th>
                  <th onClick={() => handleSortChange('publisher')}>
                    <label>Publisher {renderSortArrow('publisher')}</label>
                  </th>
                  <th onClick={() => handleSortChange('journal_page_no')}>
                    <label>Journal/Pages {renderSortArrow('journal_page_no')}</label>
                  </th>
                  <th onClick={() => handleSortChange('website_url')}>
                    <label>Website URL {renderSortArrow('website_url')}</label>
                  </th>
                  <th onClick={() => handleSortChange('accessed_date')}>
                    <label>Accessed Date {renderSortArrow('accessed_date')}</label>
                  </th>
                  <th><label>Linked Docs</label></th>
                  <th><label>Actions</label></th>
                </tr>
              </thead>
              <tbody>
                {references.map((ref) => {
                  const isEditing = editingRows.has(ref.id);
                  const hasChanges = editedData[ref.id] && Object.keys(editedData[ref.id]).length > 0;
                  const isLinkedExpanded = expandedLinkedDocs === ref.id;

                  return (
                    <tr key={ref.id}>
                      <td className="align-top">
                        <span className="ev-small-p-dark ev-bold-reg">{ref.id}</span>
                      </td>
                      <td className="align-top">{renderEditableField(ref, 'title', true, 80)}</td>
                      <td className="align-top">{renderEditableField(ref, 'authors', true, 40)}</td>
                      <td className="align-top">{renderEditableField(ref, 'year')}</td>
                      <td className="align-top">{renderEditableField(ref, 'doi', true, 30)}</td>
                      <td className="align-top">{renderEditableField(ref, 'publisher', true, 30)}</td>
                      <td className="align-top">{renderEditableField(ref, 'journal_page_no', true, 30)}</td>
                      <td className="align-top">{renderEditableField(ref, 'website_url', true, 30)}</td>
                      <td className="align-top">{renderEditableField(ref, 'accessed_date', true, 20)}</td>
                      <td className="align-top">
                        {ref.linked_docs && ref.linked_docs.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {(isLinkedExpanded
                              ? ref.linked_docs
                              : ref.linked_docs.slice(0, 1)
                            ).map((doc) => (
                              <div
                                key={doc.doc_id}
                                className="d-flex align-items-center gap-1"
                              >
                                <span
                                  className="ev-message-card1 ev-fs-10 avatar-tooltip pe-2"
                                  data-tooltip={doc.doc_name}
                                >
                                  <TrimContent text={doc.doc_name} maxLength={12} />
                                  <button
                                  onClick={() => handlePreviewDocument(doc.doc_id, doc.doc_name)}
                                  className="ev-btn-lightblue-fill-round ev-btn-xsmall text-truncate align-self-start ms-2"
                                >
                                  <i className="bi bi-eye"></i> view
                                </button>
                                </span>
                                
                              </div>
                            ))}
                            {ref.linked_docs.length > 1 && !isLinkedExpanded && (
                              <span
                                className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg avatar-tooltip"
                                onClick={() => setExpandedLinkedDocs(ref.id)}
                                data-tooltip="Show all"
                              >
                                +{ref.linked_docs.length - 1}
                              </span>
                            )}
                            {isLinkedExpanded && (
                              <span
                                className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg avatar-tooltip"
                                onClick={() => setExpandedLinkedDocs(null)}
                                // data-tooltip="Hide docs"
                              >
                                <i className="bi bi-dash"></i>
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="ev-small-p-dark">None</span>
                        )}
                      </td>
                      <td className="align-top text-center pe-0">
                        {!isEditing ? (
                          <div className="d-flex flex-row gap-1">
                            <button
                              className="btn ev-btn-blue-fill-round ev-btn-reg ev-radius-xsmall pe-2 ps-2 avatar-tooltip ev-bold-med text-nowrap me-1"
                              data-tooltip="Find Duplicates"
                              onClick={() => handleViewDuplicates(ref)}
                            >
                              <i className='bi bi-search me-1'></i>
                              Find Duplicates
                            </button>
                            <button
                              className="btn ev-btn-skyblue-fill-round ev-btn-small ev-radius-xsmall pe-2 ps-2 avatar-tooltip d-flex align-items-center justify-content-center"
                              data-tooltip="Edit Reference"
                              onClick={() => handleEditRow(ref.id)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            <button
                              className="ev-btn-lightblue-fill-round ev-btn-small me-5"
                              onClick={() => saveRowChanges(ref.id)}
                              disabled={!hasChanges}
                            >
                              <i className="bi bi-check"></i> Save
                            </button>
                            <button
                              className="ev-btn-lightblue-fill-round ev-btn-small me-5"
                              onClick={() => handleCancelEdit(ref.id)}
                            >
                              <i className="bi bi-x"></i> Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="ev-small-p-dark">
              Showing {references.length} of {total} entries
            </div>
            <div className="d-flex gap-2">
              <button
                className="ev-btn-light-fill"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="ev-btn-light-fill" disabled>
                Page {page} of {totalPages || 1}
              </button>
              <button
                className="ev-btn-light-fill"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
            <div>
              <PaginationControls
                initialSize={size}
                onSizeChange={(newSize) => {
                  setSize(newSize);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </>
      )}

      <DocumentPreview
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />

      <DuplicateReferencesModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        sourceReference={duplicateSourceRef}
        onMerge={handleMerge}
      />

      {isProcessing && <ProcessingPopup message={processingMsg} />}
      {showError && <ErrorPopup onClose={() => setShowError(false)} message={errorMsg} />}
      {showSuccess && <SuccessPopup onClose={() => setShowSuccess(false)} message={successMsg} />}
    </div>
  );
};

export default ReferenceLibrary;